import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { route } from 'preact-router';
import Hls from 'hls.js';

@observer
class StreamingTrackingOverlay extends Component {
    constructor(props) {
        super(props);
        this.state = {
            streams: [],
            showListExpanded: false,
            activeStream: null,
            activeStreamUrl: null,
            muted: true,
            volume: 0.5,
            showVolumeSlider: false,
        };
        this.pollTimer = null;
        this.hls = null;
        this.videoEl = null;
        this.volumeHideTimer = null;
    }

    formatTimeUntil = (startDate, endDate) => {
        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (now >= start && now <= end) {
            return { text: 'Live now', color: 'text-danger', icon: 'fa-circle' };
        }

        if (now > end) {
            const diffMs = now - end;
            const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
            const diffHours = Math.round(diffMs / (1000 * 60 * 60));
            const diffMinutes = Math.floor(diffMs / (1000 * 60));

            if (diffDays > 0) {
                return { text: `Ended ${diffDays} day${diffDays > 1 ? 's' : ''} ago`, color: 'text-muted', icon: 'fa-clock' };
            } else if (diffHours > 0) {
                return { text: `Ended ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`, color: 'text-muted', icon: 'fa-clock' };
            }
            return { text: `Ended ${diffMinutes} min ago`, color: 'text-muted', icon: 'fa-clock' };
        }

        const diffMs = start - now;
        const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.round(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        if (diffDays > 0) {
            return { text: `Starts in ${diffDays} day${diffDays > 1 ? 's' : ''}`, color: 'text-info', icon: 'fa-clock' };
        } else if (diffHours > 0) {
            return { text: `Starts in ${diffHours} hour${diffHours > 1 ? 's' : ''}`, color: 'text-success', icon: 'fa-clock' };
        }
        return { text: `Starts in ${diffMinutes} min`, color: 'text-success', icon: 'fa-clock' };
    }

    loadStreams = async () => {
        const { raceId } = this.props;
        if (!raceId) return;

        const { streamStore } = this.props.stores;
        const localList = await streamStore.load({
            query: { race: raceId, active: 1 },
            url: '/api/streams/public/',
            limit: 50,
            skipUpdate: true,
        });

        const FIVE_MIN_MS = 5 * 60 * 1000;
        const now = new Date();
        const filtered = (localList || []).filter((stream) => {
            if (!stream.end) return true;
            const streamEnd = new Date(stream.end);
            return !(now > streamEnd && (now - streamEnd) >= FIVE_MIN_MS);
        });

        this.setState({ streams: filtered });
    }

    toggleList = () => {
        this.setState(prevState => ({ showListExpanded: !prevState.showListExpanded }));
    }

    selectStream = (stream) => {
        const { activeStream } = this.state;
        // Toggle: if clicking the already-playing stream, stop it
        if (activeStream && activeStream.id === stream.id) {
            this.destroyHls();
            this.setState({
                activeStream: null,
                activeStreamUrl: null,
            });
            return;
        }
        // Otherwise, switch to the new stream
        this.destroyHls();
        this.setState({
            activeStream: stream,
            activeStreamUrl: stream.streamUrl,
            showListExpanded: false,
        });
    }

    closePlayer = (e) => {
        e.stopPropagation();
        this.destroyHls();
        this.setState({
            activeStream: null,
            activeStreamUrl: null,
        });
    }

    toggleVolumeSlider = (e) => {
        e.stopPropagation();
        this.setState(prev => {
            const willShow = !prev.showVolumeSlider;
            if (willShow) {
                this.startVolumeHideTimer();
            } else {
                this.clearVolumeHideTimer();
            }
            return { showVolumeSlider: willShow };
        });
    }

    startVolumeHideTimer = () => {
        this.clearVolumeHideTimer();
        this.volumeHideTimer = setTimeout(() => {
            this.setState({ showVolumeSlider: false });
        }, 2000);
    }

    clearVolumeHideTimer = () => {
        if (this.volumeHideTimer) {
            clearTimeout(this.volumeHideTimer);
            this.volumeHideTimer = null;
        }
    }

    handleVolumeChange = (e) => {
        e.stopPropagation();
        const val = parseFloat(e.target.value);
        if (!this.videoEl) return;
        this.videoEl.volume = val;
        this.videoEl.muted = val === 0;
        this.setState({ volume: val, muted: val === 0 });
        this.startVolumeHideTimer();
    }

    goToStreamingPage = () => {
        const { raceId } = this.props;
        route(`/race/${raceId}/streaming`);
    }

    initHls = (el) => {
        if (!el) return;
        this.videoEl = el;
        const { activeStreamUrl } = this.state;
        if (!activeStreamUrl) return;

        if (Hls.isSupported()) {
            this.hls = new Hls({
                enableWorker: true,
                lowLatencyMode: true,
            });
            this.hls.loadSource(activeStreamUrl);
            this.hls.attachMedia(el);
            this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
                el.play().catch(() => {});
            });
        } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
            el.src = activeStreamUrl;
            el.addEventListener('loadedmetadata', () => {
                el.play().catch(() => {});
            });
        }
    }

    destroyHls = () => {
        if (this.hls) {
            this.hls.destroy();
            this.hls = null;
        }
        if (this.videoEl) {
            this.videoEl.pause();
            this.videoEl.removeAttribute('src');
            this.videoEl.load();
            this.videoEl = null;
        }
    }

    componentDidMount() {
        this.loadStreams();
        this.pollTimer = setInterval(() => {
            this.loadStreams();
        }, 120000);
    }

    componentWillUnmount() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
        this.clearVolumeHideTimer();
        this.destroyHls();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.raceId !== this.props.raceId) {
            this.loadStreams();
        }
    }

    render() {
        const { streams, showListExpanded, activeStream, activeStreamUrl, muted, volume, showVolumeSlider } = this.state;

        if (!streams || streams.length === 0) {
            return null;
        }

        const now = new Date();

        return (
            <div
                class='position-absolute'
                style={`bottom: ${this.props.bottom || '20px'}; right: 0; z-index: 100001; pointer-events: none;`}
            >
                <div class='d-flex flex-column align-items-end'>
                    {/* Mini player */}
                    {activeStream && activeStreamUrl && (
                        <div
                            class='stream-mini-player mb-1 me-0 position-relative'
                            style='background: #000; border-radius: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.4); pointer-events: auto; cursor: pointer;'
                            onClick={this.closePlayer}
                        >
                            <video
                                ref={this.initHls}
                                muted
                                playsinline
                                style='width: 100%; height: 100%; object-fit: cover;'
                            />
                            {/* Close button */}
                            <div
                                class='position-absolute d-flex align-items-center justify-content-center stream-close-btn'
                                style='background: rgba(0,0,0,0.6); border-radius: 50%; cursor: pointer;'
                                onClick={this.closePlayer}
                            >
                                <i class='fa-solid fa-xmark text-white stream-close-icon' />
                            </div>
                            {/* Stream name label */}
                            <div
                                class='position-absolute d-flex align-items-center stream-label-bar'
                                style='bottom: 0; left: 0; right: 0; background: linear-gradient(transparent, rgba(0,0,0,0.7));'
                            >
                                {(() => {
                                    const sd = new Date(activeStream.start);
                                    const ed = new Date(activeStream.end);
                                    const isLive = activeStream.start && activeStream.end && sd < now && ed > now;
                                    return isLive ? (
                                        <span class='badge bg-danger me-1 stream-live-badge'>
                                            LIVE
                                        </span>
                                    ) : null;
                                })()}
                                <span class='text-white text-truncate stream-label-text'>
                                    {activeStream.name}
                                </span>
                            </div>
                            {/* Volume icon + popup slider */}
                            <div
                                class='position-absolute stream-volume-icon-wrapper'
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button
                                    type='button'
                                    class='btn btn-sm p-0 d-flex align-items-center justify-content-center stream-volume-icon-btn'
                                    style='background: rgba(0,0,0,0.6); border-radius: 50%; border: none;'
                                    onClick={this.toggleVolumeSlider}
                                    title='Volume'
                                >
                                    <i class={`fa-solid text-white ${muted || volume === 0 ? 'fa-volume-xmark' : volume < 0.5 ? 'fa-volume-low' : 'fa-volume-high'}`} />
                                </button>
                                {showVolumeSlider && (
                                    <div
                                        class='position-absolute rounded stream-volume-popup'
                                        style='bottom: 100%; left: 50%; transform: translateX(-50%); margin-bottom: 6px; background-color: rgba(0, 0, 0, 0.8); display: flex; align-items: center; justify-content: center;'
                                    >
                                        <input
                                            type='range'
                                            class='form-range'
                                            style='width: 80px; transform: rotate(-90deg);'
                                            min='0'
                                            max='1'
                                            step='0.05'
                                            value={muted ? 0 : volume}
                                            onInput={this.handleVolumeChange}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Expanded stream list */}
                    {showListExpanded && (
                        <div
                            class='stream-expanded-list mb-1 me-0'
                            style='max-height: 400px; overflow-y: auto; pointer-events: auto; background: rgba(0,0,0,0.7); border-radius: 4px; min-width: 300px; max-width: 500px;'
                        >
                            {streams.map((stream) => {
                                const sd = new Date(stream.start);
                                const ed = new Date(stream.end);
                                const isLive = stream.start && stream.end && sd < now && ed > now;
                                const timeInfo = stream.start ? this.formatTimeUntil(stream.start, stream.end) : null;
                                return (
                                    <div
                                        key={stream.id}
                                        class='stream-list-item d-flex flex-column py-2 px-3'
                                        style='cursor: pointer; border-bottom: 1px solid rgba(255,255,255,0.1);'
                                        onClick={() => this.selectStream(stream)}
                                    >
                                        <div class='d-flex align-items-center'>
                                            {isLive ? (
                                                <span class='badge bg-danger me-2' style='font-size: 0.6rem; padding: 2px 5px;'>
                                                    LIVE
                                                </span>
                                            ) : (
                                                <i class='fa-solid fa-video text-white-50 me-2' style='font-size: 10px;' />
                                            )}
                                            <span class='text-white text-truncate' style='font-size: 0.8rem;'>
                                                {stream.name}
                                            </span>
                                        </div>
                                        {timeInfo && (
                                            <small class='text-white-50 ms-4' style='font-size: 0.7rem;'>
                                                <i class={`fa-solid ${timeInfo.icon} me-1`} style='font-size: 0.55rem;' />
                                                {timeInfo.text}
                                            </small>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Toggle button */}
                    <div
                        class='d-inline-flex align-items-center py-1 px-3'
                        style='background: rgba(0,0,0,0.7); pointer-events: auto; cursor: pointer; white-space: nowrap; border-radius: 4px 0 0 4px;'
                        onClick={this.toggleList}
                    >
                        {showListExpanded ? (
                            <span class='text-white' style='font-size: 0.8rem;'>
                                <i class='fa-solid fa-chevron-down stream-btn-icon' style='font-size: 0.6rem;' />
                                <span class='stream-btn-label'>Skjul</span>
                            </span>
                        ) : (
                            <span class='text-white' style='font-size: 0.8rem;'>
                                <i class='fa-solid fa-video stream-btn-icon' style='font-size: 0.6rem;' />
                                <span class='stream-btn-label'>Streaming ({streams.length})</span>
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default StreamingTrackingOverlay;
